import clsx from 'clsx';
import { type ReactNode } from 'react';
import { HintField } from './hint-field';

type FieldAs = 'input' | 'textarea' | 'select';
type FieldType = 'text' | 'email' | 'tel' | 'number';

export type FieldChangeEvent = React.ChangeEvent<{ name: string; value: string }>;
export type FieldBlurEvent = React.FocusEvent<{ name: string }>;

type FieldProps = {
  name: string;
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  type?: FieldType;
  as?: FieldAs;
  colSpan?: 1 | 2;
  min?: number;
  children?: ReactNode;
  className?: string;
  onChange: (event: FieldChangeEvent) => void;
  onBlur?: (event: FieldBlurEvent) => void;
};

const controlBase =
  'peer w-full rounded-(--radius-current) bg-transparent px-4 pt-6 pb-2 text-dark-blue outline-none border transition-colors duration-200';

const borderStates = (hasError: boolean) =>
  hasError ? 'border-red-400' : 'border-dark-blue/15 hover:border-dark-blue/30 focus:border-orange';

const labelBase =
  'pointer-events-none absolute left-4 origin-left max-w-full truncate transition-all duration-200';

const labelRest = 'top-[17px] text-base text-dark-blue/60';

const labelFloated = 'top-2 text-xs';

const labelFocusFloat = 'peer-focus:top-2 peer-focus:text-xs peer-focus:text-orange';

const labelFilledFloat =
  'peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs';

export function Field({
  name,
  label,
  value,
  error,
  required,
  type = 'text',
  as = 'input',
  colSpan = 1,
  min,
  children,
  className,
  onChange,
  onBlur,
}: FieldProps) {
  const hasError = Boolean(error);
  const isFilled = value !== '' && value !== undefined && value !== null;

  const wrapperClass = clsx(
    'relative',
    colSpan === 2 && 'col-span-2',
    as === 'textarea' && 'pb-1',
    className,
  );

  const renderLabel = (floatedAlways: boolean) => (
    <label
      htmlFor={name}
      className={clsx(
        labelBase,
        floatedAlways || isFilled ? clsx(labelFloated, 'text-dark-blue/60') : labelRest,
        !floatedAlways && labelFocusFloat,
        !floatedAlways && labelFilledFloat,
        hasError && 'text-red-500',
      )}
    >
      {label}
      {required && <span className="text-orange ml-0.5">*</span>}
    </label>
  );

  if (as === 'textarea') {
    return (
      <div className={wrapperClass}>
        <textarea
          className={clsx(controlBase, borderStates(hasError), 'h-28 resize-none')}
          id={name}
          name={name}
          placeholder=" "
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
        {renderLabel(false)}
        <HintField message={error} variant="error" />
      </div>
    );
  }

  if (as === 'select') {
    return (
      <div className={wrapperClass}>
        <select
          id={name}
          name={name}
          value={value}
          className={clsx(
            controlBase,
            borderStates(hasError),
            'cursor-pointer appearance-none pr-6',
            !isFilled && 'text-dark-blue/40',
          )}
          onBlur={onBlur}
          onChange={onChange}
        >
          {children}
        </select>
        <span
          aria-hidden="true"
          className="text-dark-blue/40 pointer-events-none absolute top-1/2 right-4 -translate-y-1/2"
        >
          <svg
            fill="none"
            height="10"
            viewBox="0 0 12 8"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </span>
        {renderLabel(true)}
        <HintField message={error} variant="error" />
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <input
        className={clsx(controlBase, borderStates(hasError))}
        id={name}
        min={min}
        name={name}
        placeholder=" "
        required={required}
        type={type}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
      />
      {renderLabel(false)}
      <HintField message={error} variant="error" />
    </div>
  );
}
